import classnames from 'classnames'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import styles from './ProductPageGalleryView.module.scss'
import useStore from '@/store'
import SanityImage from '@/components/SanityImage/SanityImage'
import AnimatedTextLines from '@/components/AnimatedTextLines/AnimatedTextLines'
import gsap from 'gsap'
import { Flip } from 'gsap/dist/Flip'
import { wait } from '@/utils'
import useCurrentPage from '@/hooks/use-current-page'
import { ScrollContext } from '@/context/Scroll'
import useBreakpoint from '@/hooks/use-breakpoint'
import { PRODUCT_PAGE_GALLERY_OPEN_BUTTON_ID } from '@/sections/ProductPageUi/ProductPageUiLeftSide/ProductPageUiLeftSide'
import { focusElement } from '@/utils'
import FocusLock from 'react-focus-lock'
import LineButton from '@/components/LineButton/LineButton'

gsap.registerPlugin(Flip)

const SMALL_BREAKPOINTS = {
  tablet: {
    width: 600,
  },
  xs: {
    width: 300,
  },
}

const LARGE_BREAKPOINTS = {
  tablet: {
    width: 800,
  },
  xs: {
    width: 800,
  },
}

export const PRODUCT_PAGE_GALLERY_CLOSE_BUTTON_ID = 'gallery-close-button'

const ProductPageGalleryView = ({ className }) => {
  const productGalleryImagesOpen = useStore(state => state.productGalleryImagesOpen)
  const productPageData = useStore(state => state.productPageData)
  const closeButtonRef = useRef(null)
  const indexLinesRef = useRef(null)
  const productTitleLinesRef = useRef(null)
  const $imageContainers = useRef([])
  const $imageContainerInners = useRef([])
  const $positionBoxes = useRef([])
  const setCursorState = useStore(state => state.setCursorState)
  const closeButtonLinesRef = useRef(null)
  const [imageStackImages, setImageStackImages] = useState([])
  const [activeImageId, setActiveImageId] = useState(0)
  const imageStackContainerRef = useRef(null)
  const { isProductInnerPage } = useCurrentPage()
  const setProductGalleryImagesOpen = useStore(state => state.setProductGalleryImagesOpen)
  const $container = useRef()
  const [render, setRender] = useState(false)
  const [initialTitleText, setInitialTitleText] = useState('')
  const animateVisibilityTimeout = useRef()
  const isVisibleRef = useRef(false)
  const bgRef = useRef(null)
  const { isMobile } = useBreakpoint()
  const { scroll } = useContext(ScrollContext)
  const $largeImagesContainer = useRef(null)
  const galleryImages = useMemo(() => {
    if (!productPageData?.productData?.galleryImages?.length) return []
    return productPageData?.productData?.galleryImages
  }, [productPageData])
  const productsInGroups = useMemo(() => {
    const items = galleryImages
    if (!items?.length) return []

    return items.map((item, index) => {
      // Favor left and right groups, keep center minimal
      let group = 'center'
      const totalItems = items.length

      if (index < Math.ceil(totalItems / 2) - 1) {
        group = 'left'
      } else if (index > Math.floor(totalItems / 2)) {
        group = 'right'
      }

      return {
        item,
        group,
      }
    })
  }, [galleryImages])

  const centerItem = useMemo(() => {
    return productsInGroups.find(item => item.group === 'center')
  }, [productsInGroups])

  useEffect(() => {
    if (centerItem && productGalleryImagesOpen) {
      setActiveImageId(centerItem.item.image?.asset?._id)
    }
  }, [centerItem, productGalleryImagesOpen])

  // console.log(galleryImages)

  // console.log(productsInGroups)

  useEffect(() => {
    let _imageStackImages = []
    if (!!galleryImages?.length) {
      _imageStackImages = galleryImages.map(item => item.image)
    }

    setImageStackImages(_imageStackImages)
  }, [productPageData, galleryImages])

  useEffect(() => {
    if (productPageData) {
      if (productTitleLinesRef.current) {
        productTitleLinesRef.current.updateText(productPageData.title)
        productTitleLinesRef.current.reset()
      }
      setInitialTitleText(productPageData.title)
    }
  }, [productPageData])

  useEffect(() => {
    if (!isProductInnerPage) {
      setProductGalleryImagesOpen(false)
    } else {
      setRender(true)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProductInnerPage])

  const animateVisibility = async (isVisible = true, callback) => {
    if (isVisibleRef.current === isVisible) return

    isVisibleRef.current = isVisible

    if (isVisible) {
      await wait(200)
    }

    const cardsDuration = isVisible ? 1.2 : 0.9

    setTimeout(
      () => {
        if (callback) callback()
      },
      cardsDuration * 1000 + 100,
    )

    if (bgRef.current) {
      gsap.killTweensOf(bgRef.current)
      gsap.to(bgRef.current, {
        opacity: isVisible ? 1 : 0,
        duration: 0.8,
        ease: 'Power4.easeOut',
      })
    }

    if ($largeImagesContainer.current) {
      gsap.killTweensOf($largeImagesContainer.current)
      gsap.to($largeImagesContainer.current, {
        autoAlpha: isVisible ? 1 : 0,
        duration: isVisible ? 1.2 : 0.4,
        delay: isVisible ? 0.3 : 0,
        ease: 'Power4.easeOut',
      })
    }

    if (closeButtonRef.current) {
      if (isVisible) {
        closeButtonRef.current.animateIn()
      } else {
        closeButtonRef.current.animateOut()
      }
    }

    // Animate images to positions
    $imageContainerInners.current.forEach((el, i) => {
      if (!el) return

      const positionBox = $positionBoxes.current[i]
      const parentContainer = $imageContainers.current[i]

      const startFromEl = isVisible ? positionBox : parentContainer
      const endToEl = isVisible ? parentContainer : positionBox

      if (positionBox && parentContainer) {
        Flip.fit(el, startFromEl, {
          scale: true,
        })

        let delay = isVisible ? 0.1 * i : 0.05 * i

        if (isMobile && !isVisible) {
          delay = 0
        }

        Flip.fit(el, endToEl, {
          scale: true,
          duration: cardsDuration,
          // delay: isVisible ? 0.1 * i : 0,
          // delay: isVisible ? 0.1 * i : 0.05 * i,
          delay,
          ease: 'Power4.easeOut',
        })

        if (isVisible) {
          gsap.set(parentContainer, {
            opacity: 1,
          })
        }
      }
    })

    if (!closeButtonLinesRef.current) return

    closeButtonLinesRef.current[isVisible ? 'animate' : 'animateOut']()
    indexLinesRef.current[isVisible ? 'animate' : 'animateOut']()

    if (productTitleLinesRef.current) {
      productTitleLinesRef.current[isVisible ? 'animate' : 'animateOut']({
        delay: isVisible ? 0.1 : 0,
      })
    }

    if (imageStackContainerRef.current) {
      gsap.killTweensOf(imageStackContainerRef.current)
      gsap.to(imageStackContainerRef.current, {
        '--y': isVisible ? '0%' : '100%',
        duration: isVisible ? 1.2 : 0.4,
        delay: isVisible ? 0.4 : 0,
        ease: isVisible ? 'Power4.easeInOut' : 'Power4.easeOut',
      })
    }
  }

  useEffect(() => {
    if (!imageStackImages?.length) return

    if (animateVisibilityTimeout.current) {
      clearTimeout(animateVisibilityTimeout.current)
    }

    animateVisibilityTimeout.current = setTimeout(() => {
      scroll[productGalleryImagesOpen ? 'stop' : 'start']()

      animateVisibility(productGalleryImagesOpen, () => {
        if (!productGalleryImagesOpen && !isProductInnerPage) {
          setRender(false)
        }
      })
    }, 50)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productGalleryImagesOpen, isProductInnerPage, scroll, imageStackImages?.length])

  const handleClose = () => {
    setProductGalleryImagesOpen(false)
    setActiveImageId(null)
    setTimeout(() => {
      focusElement(document.getElementById(PRODUCT_PAGE_GALLERY_OPEN_BUTTON_ID))
    }, 100)
  }

  if (!imageStackImages?.length || !render) return

  return (
    <div
      className={classnames(styles.ProductPageGalleryView, className, {
        [styles.productGalleryImagesOpen]: productGalleryImagesOpen,
      })}
      aria-hidden={!productGalleryImagesOpen}
      ref={$container}
      data-image-count={galleryImages?.length}
    >
      <div className={styles.content}>
        <div
          className={styles.bg}
          ref={bgRef}
          onClick={handleClose}
          onMouseEnter={() => {
            if (isMobile) return
            setCursorState('Close')
          }}
          onMouseLeave={() => {
            if (isMobile) return
            setCursorState(null)
          }}
        />
        <div
          className={styles.largeImagesContainer}
          ref={$largeImagesContainer}
        >
          <LargeImageContainer
            className={styles.largeImagesLeft}
            images={productsInGroups.filter(item => item.group === 'left').map(item => item.item.image)}
            activeImageId={activeImageId}
            galleryIsClosed={!productGalleryImagesOpen}
          />
          <LargeImageContainer
            className={styles.largeImagesCenter}
            images={productsInGroups.filter(item => item.group === 'center').map(item => item.item.image)}
            activeImageId={activeImageId}
            galleryIsClosed={!productGalleryImagesOpen}
          />
          <LargeImageContainer
            className={styles.largeImagesRight}
            images={productsInGroups.filter(item => item.group === 'right').map(item => item.item.image)}
            activeImageId={activeImageId}
            galleryIsClosed={!productGalleryImagesOpen}
          />
        </div>
        <div className={styles.topContent}>
          <FocusLock disabled={!productGalleryImagesOpen}>
            <LineButton
              ref={closeButtonRef}
              className={styles.closeButton}
              onClick={handleClose}
              id={PRODUCT_PAGE_GALLERY_CLOSE_BUTTON_ID}
              tabIndex={productGalleryImagesOpen ? 0 : -1}
              label="Close"
              showInitially={false}
            />
          </FocusLock>
          <div className={styles.indexAndTitleContainer}>
            <AnimatedTextLines
              ref={indexLinesRef}
              querySelector="span"
              autoAnimate={false}
              className={classnames(styles.indexLines)}
            >
              <span className={styles.indexValue}>(01)</span>
            </AnimatedTextLines>
            {initialTitleText && (
              <AnimatedTextLines
                ref={productTitleLinesRef}
                querySelector="span"
                autoAnimate={false}
                className={classnames(styles.productTitleLines)}
                toAnimate="lines"
              >
                <span className={styles.productTitleValue}>{initialTitleText}</span>
              </AnimatedTextLines>
            )}
          </div>
        </div>
        <ul className={styles.list}>
          {galleryImages?.map((item, i) => (
            <li
              key={`${item?.image?.asset?.url}-${i}`}
              className={styles.imageContainer}
              ref={el => {
                $imageContainers.current[i] = el
              }}
              onMouseEnter={() => {
                if (isMobile) return
                setActiveImageId(item?.image?.asset?._id)
              }}
              onClick={() => {
                if (!isMobile) return
                setActiveImageId(item?.image?.asset?._id)
              }}
            >
              <div
                className={styles.imageContainerInner}
                ref={el => {
                  $imageContainerInners.current[i] = el
                }}
                onMouseEnter={() => {
                  if (item?.material?.title) {
                    setCursorState(item?.material?.title)
                  }
                }}
                onMouseLeave={() => {
                  setCursorState(null)
                }}
              >
                <SanityImage
                  className={styles.image}
                  image={item.image}
                  breakpoints={SMALL_BREAKPOINTS}
                />
              </div>
            </li>
          ))}
        </ul>
        <div className={styles.positionBoxesContainer}>
          {Array.from({ length: galleryImages?.length }).map((_, i) => (
            <div
              key={i}
              ref={el => {
                $positionBoxes.current[i] = el
              }}
              className={styles.positionBox}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

const LargeImageContainer = ({ className, images, activeImageId, galleryIsClosed }) => {
  return (
    <div className={className}>
      {images.map((image, i) => (
        <div
          className={classnames(styles.largeImage__container, {
            [styles.active]: activeImageId === image?.asset?._id && !galleryIsClosed,
          })}
          key={`${image?.asset?.url}-${i}`}
        >
          <SanityImage
            className={styles.largeImage__image}
            image={image}
            breakpoints={LARGE_BREAKPOINTS}
            disableLoadedOpacity={true}
          />
        </div>
      ))}
    </div>
  )
}

ProductPageGalleryView.displayName = 'ProductPageGalleryView'

export default ProductPageGalleryView
